/**
 * ENG-9341 — one door for every Composio HTTP call.
 *
 * ## Why a seam at all
 *
 * Composio calls are spread across the API, the webapp's admin routes and core.
 * Each one re-decides the base URL, the auth header shape, the timeout and what
 * a 4xx means. That divergence is not hypothetical: `composio-account-probe.ts`
 * maps EVERY 4xx to "reconnect required", so an IP-restriction rejection is
 * indistinguishable there from a deleted account — which is the defect ENG-9341
 * was filed about.
 *
 * ## Why this file is worth little without the guard beside it
 *
 * A seam everyone is asked politely to use is a convention, and conventions
 * decay one new `fetch` at a time. `scripts/check-composio-raw-fetch.mjs` is
 * what makes it a constraint, and it is the deliverable this ticket actually
 * asked for. This module exists so that guard has somewhere to point.
 *
 * ## What it does, and the one thing it deliberately still does not
 *
 * It centralises the base URL, the API-key header, a bounded timeout, a
 * structured result, and — since the callers came through the door — the
 * classification of a failure into a {@link ConnectivityCause}. It still does
 * NOT add retries or credential healing: those change behaviour at every call
 * site at once, and nothing has asked for them.
 *
 * ## Why the classification is STRUCTURAL, not a match on the body
 *
 * ENG-9770 already settled this one directory over, in
 * `composio-account-probe.ts`: the discriminator is WHICH CREDENTIAL WAS
 * REFUSED, not what the error body says. Every caller of this seam sends OUR
 * platform key, so a 401/403 means our access was refused — whether by an IP
 * allowlist, a revoked key or a wrong project is a diagnostic detail no caller
 * routes on. That is why {@link ComposioFetchResult} carries
 * `cause: 'access_restricted'` for the pair and leaves the specifics to the
 * body snippet, which is informative and never load-bearing.
 *
 * ENG-9341's issue text anticipated a seam that would branch on a 401 body
 * (hence its warning about the U+2019 apostrophe in Composio's wording). This
 * seam does not branch on the body, so that trap does not apply here. It still
 * applies — and is still handled — in `composio-ip-protection-probe.ts`, which
 * asks a genuinely different question: is this key protected from EVERYONE
 * ELSE? A revoked key also answers 401, and only the body separates the two.
 * That is the one place body markers are correct, and it is exempt from this
 * seam for the reason below.
 *
 * ## One caller must never come through it
 *
 * `composio-ip-protection-probe.ts` measures whether an un-allowlisted origin is
 * REFUSED. Its meaning depends on the request travelling unassisted; route it
 * through a helper free to add retries or a future egress path and the
 * measurement stops meaning anything, without erroring. It calls an injected
 * `fetchImpl` rather than `fetch`, so the guard does not flag it — but the
 * exemption marker exists for the day something like it does.
 */

import type { ConnectivityCause } from './integration-health.js';
import {
  isComposioBridgeError,
  type ComposioBridgeErrorKind,
} from './composio-egress-bridge.js';

/** Default Composio API origin. Overridable for local/staging proxies. */
export const COMPOSIO_API_BASE = 'https://backend.composio.dev';

/**
 * Bounded so one hung socket cannot stall a request handler or a cron tick.
 *
 * 30s → 45s in ENG-10691, and NOT for the seam's own sake. It is the outermost layer
 * of the bridge's timeout chain, so it had to move for the handler's ceiling to reach
 * 33s — which is what stops a Composio call that works today from failing the moment
 * a key is allowlisted. The consequence is real and belongs here rather than in the
 * ticket: a pathological hang on ANY Composio REST call, including the interactive
 * connect path, now occupies a request for up to 45s instead of 30s.
 */
export const COMPOSIO_DEFAULT_TIMEOUT_MS = 45_000;

export interface ComposioFetchOptions {
  /** HTTP method. Defaults to GET. */
  method?: string;
  /** Composio API key. Sent as `x-api-key`, the header Composio expects. */
  apiKey?: string | null;
  /** JSON body. Serialised here so no caller re-decides the content type. */
  body?: unknown;
  /** Extra headers, merged over the defaults. */
  headers?: Record<string, string>;
  /** Overrides {@link COMPOSIO_DEFAULT_TIMEOUT_MS}. */
  timeoutMs?: number;
  /** Overrides {@link COMPOSIO_API_BASE}; useful for a staging proxy. */
  baseUrl?: string;
  /**
   * A caller-owned abort signal, composed WITH the seam's own timeout rather
   * than replacing it. Both remain in force: whichever fires first wins, so a
   * caller cancelling a request cannot accidentally remove the socket ceiling,
   * and the ceiling cannot outlive a cancelled request.
   */
  signal?: AbortSignal;
  /** Injectable for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

/**
 * The outcome of a Composio call, as a value rather than a thrown error.
 *
 * A thrown timeout and a 500 are the same problem to most callers and are
 * currently handled two different ways at each site. Returning both as a
 * discriminated result is what lets a future classification live in one place —
 * see the module header on why that classification is NOT here yet.
 */
export type ComposioFetchResult =
  | { ok: true; status: number; statusText: string; body: string }
  | {
      ok: false;
      status: number;
      /**
       * The reason phrase, carried because several callers' error messages fall
       * back to it when the body has no `message`. Cheap to keep and lossy to
       * drop — a bare status code is a worse message than "404 Not Found".
       */
      statusText: string;
      body: string;
      reason: 'http_error';
      cause: ComposioFailureCause;
      /**
       * A short, api-key-redacted quote of the error body, for MESSAGES only.
       * Nothing branches on it — see the module header on why classification is
       * structural. Present only when the body was non-empty.
       */
      bodySnippet?: string;
    }
  | {
      ok: false;
      status: 0;
      statusText: '';
      body: '';
      reason: 'timeout' | 'network_error';
      cause: 'unreachable';
      error: string;
    }
  /**
   * OUR transport failed, not Composio (ENG-9924).
   *
   * Separated from the two above because every other verdict this seam issues
   * is a statement about the vendor. An invoke throttle graded `unreachable`
   * blames Composio for our outage; a synthesized 5xx would grade
   * `server_error`, which `isVendorSideCause` suppresses from actionable
   * alerting — so a bridge outage would page nobody. `cause` is `unknown`
   * because `integration-health.ts` deliberately treats an unclassified
   * failure as actionable ("failing towards tell someone").
   */
  | {
      ok: false;
      status: 0;
      statusText: '';
      body: '';
      reason: 'bridge_error';
      cause: 'unknown';
      error: string;
      bridgeKind: ComposioBridgeErrorKind;
    };

/**
 * The subset of {@link ConnectivityCause} this seam can establish from one
 * response. Deliberately a subset: `semantic`, `reconnect_in_progress` and the
 * rest are judgements about an integration, which a transport helper is not in
 * a position to make.
 */
export type ComposioFailureCause = Extract<
  ConnectivityCause,
  'access_restricted' | 'rate_limited' | 'server_error' | 'unknown'
>;

/**
 * Resolves the transport for the NEXT call, or `null` to use global `fetch`.
 *
 * A RESOLVER consulted per call, not a value set once at boot, and that
 * distinction is the whole rollback story (ENG-9924 / AC7). The arming lever is
 * an SSM parameter read through `getSecret`, whose ~5-minute TTL is evaluated
 * inside each call (`secrets.ts:66`). Freeze the decision at container start
 * and turning the bridge off would instead wait on Lambda recycling — unbounded
 * under steady traffic, and precisely when you least want to wait.
 *
 * It is also what makes this testable: a resolver can be cleared in `afterEach`,
 * where a mutated module default leaks into the next test.
 */
type ComposioTransportResolver = () => Promise<typeof fetch | null>;

let transportResolver: ComposioTransportResolver | null = null;

/**
 * Register the transport resolver for THIS process.
 *
 * Must be called in every process that egresses to Composio — there is more
 * than one, and assuming otherwise is how half the fleet keeps talking direct.
 * Today: the API Lambda, the Next.js/OpenNext server Lambda (five admin routes
 * import this seam), and the crons that construct a `ComposioAdapter`. A
 * process that never registers keeps using global `fetch`, which is correct
 * before the allowlist is armed and a silent outage after it.
 */
export function setComposioTransportResolver(resolver: ComposioTransportResolver): void {
  transportResolver = resolver;
}

/** Drop the resolver. For tests, and for a process that must never bridge. */
export function clearComposioTransportResolver(): void {
  transportResolver = null;
}

/**
 * Never throws. A resolver that fails leaves the call on global `fetch` rather
 * than failing the request — fail-open, which is right before the allowlist is
 * armed. NOTE the direction inverts once Part 3 arms it: direct egress becomes
 * the leg that gets refused, so this log line is the thing that explains a
 * sudden fleet-wide `access_restricted`.
 */
export async function getComposioTransport(): Promise<typeof fetch | null> {
  if (!transportResolver) return null;
  try {
    return await transportResolver();
  } catch (err) {
    console.error('[composio] transport resolver failed; using direct egress', {
      error: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
}

/** Longest error body this will quote back. Mirrors the account probe's ceiling. */
const BODY_SNIPPET_MAX = 300;

/**
 * Classify an HTTP failure status.
 *
 * The 401/403 PAIR, not 401 alone. Composio's live allowlist rejection is a 401
 * (captured verbatim under ENG-9683, correcting the vendor docs' 403), but the
 * response is served through Cloudflare and Vercel, so an edge layer can answer
 * a different code for the same condition without Composio changing anything.
 * `composio-account-probe.ts` treats the pair as one case for exactly this
 * reason; splitting them here would make the two disagree about the same
 * response.
 *
 * 404 is deliberately `unknown` rather than a cause of its own: on this API it
 * means the addressed entity is gone, which is a fact about the CALLER's
 * request, not about our access. Callers that care already read `status` — and
 * keeping 404 out of `access_restricted` is the entire point of AC4, since
 * conflating them is what made an IP-restricted key read as a deleted account.
 */
export function classifyComposioFailure(status: number): ComposioFailureCause {
  if (status === 401 || status === 403) return 'access_restricted';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

/**
 * Header names whose VALUE is a credential and must be redacted from a quoted
 * body. Matched on the name, not the value, because a value-shaped heuristic is
 * exactly the "pattern-based scrubber that misses one shape" this file rejects.
 *
 * Not hypothetical: `createProject`, `deleteProject` and `regenerateProjectApiKey`
 * authenticate with the ORG key via `x-org-api-key`, so `apiKey` is undefined
 * for them and redacting only `apiKey` would leave an echoed org key in plain
 * text — in a field whose own doc comment says it is safe to put in a message.
 * (CodeRabbit, PR #5487.)
 */
const CREDENTIAL_HEADER_RE = /(^|-)(api-?key|key|token|secret|authorization|auth)$/i;

/**
 * Quote an error body for a human, with every credential the request SENT
 * removed.
 *
 * Redaction is by EXACT MATCH of the values we sent, not by a general secret
 * detector: the realistic leak here is the vendor echoing our own credential
 * back, and a pattern-based scrubber that misses one shape is worse than a
 * narrow rule that cannot. Lifted from `composio-account-probe.ts`'s
 * `readErrorBodySnippet`, which made the same call for the same reason.
 */
function snippet(body: string, secrets: Array<string | null | undefined>): string | undefined {
  if (!body) return undefined;
  let redacted = body;
  for (const secret of secrets) {
    // A short value would match everywhere; only redact something credential-
    // length, so a stray header cannot mangle the whole snippet.
    if (secret && secret.length >= 8) redacted = redacted.split(secret).join('[redacted]');
  }
  const collapsed = redacted.replace(/\s+/g, ' ').trim();
  if (!collapsed) return undefined;
  return collapsed.length > BODY_SNIPPET_MAX
    ? `${collapsed.slice(0, BODY_SNIPPET_MAX)}…`
    : collapsed;
}

/**
 * Call the Composio API.
 *
 * `path` is joined to the base, so callers pass `/api/v3/auth_configs`, never a
 * full URL — which is also what keeps the guard's job simple: a `composio.dev`
 * string outside this module is, by construction, a call that did not come
 * through the door.
 */
export async function composioFetch(
  path: string,
  options: ComposioFetchOptions = {},
): Promise<ComposioFetchResult> {
  const {
    method = 'GET',
    apiKey,
    body,
    headers = {},
    timeoutMs = COMPOSIO_DEFAULT_TIMEOUT_MS,
    baseUrl = COMPOSIO_API_BASE,
    signal: callerSignal,
    fetchImpl: explicitFetchImpl,
  } = options;

  // Precedence: an explicitly injected impl always wins, so a test double and
  // `composio-ip-protection-probe`'s deliberately-unassisted request are never
  // silently rerouted through the bridge — that probe's measurement depends on
  // travelling unaided, and a bridged one would report the whole fleet
  // protected without erroring.
  const fetchImpl = explicitFetchImpl ?? (await getComposioTransport()) ?? fetch;

  const url = `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const signal = callerSignal
    ? AbortSignal.any([controller.signal, callerSignal])
    : controller.signal;

  try {
    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(apiKey ? { 'x-api-key': apiKey } : {}),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      signal,
    });
    const text = await res.text();
    if (res.ok) return { ok: true, status: res.status, statusText: res.statusText, body: text };
    const snip = snippet(text, [
      apiKey,
      ...Object.entries(headers)
        .filter(([name]) => CREDENTIAL_HEADER_RE.test(name))
        .map(([, value]) => value),
    ]);
    return {
      ok: false,
      status: res.status,
      statusText: res.statusText,
      body: text,
      reason: 'http_error',
      cause: classifyComposioFailure(res.status),
      ...(snip ? { bodySnippet: snip } : {}),
    };
  } catch (err) {
    // A timeout is reported apart from a network error on purpose. They look
    // identical from a `catch` and mean different things: one is Composio being
    // slow, the other is not reaching it at all — and collapsing them is how
    // `composio-account-probe` ended up calling a protected key a dead one.
    // Abort-shaped errors are timeouts, with ONE exception: a cancellation the
    // CALLER initiated. Reporting that as a Composio timeout would blame the
    // vendor for our own cancellation.
    //
    // `TimeoutError` is matched as well as `AbortError` because a `fetchImpl`
    // built on `AbortSignal.timeout` throws the former — dropping it would
    // silently regrade a real timeout as a network error.
    // BEFORE the abort check: the AWS SDK throws `TimeoutError` for its own
    // request timeout, which the abort branch below would grade as "Composio is
    // slow" when the truth is that our bridge timed out.
    // A cancellation the CALLER initiated is not a fault of anyone's. The seam
    // composes `callerSignal` into the signal handed to `fetchImpl`, the bridge
    // forwards it to the invoke, and the invoke reports the resulting abort as
    // `invoke_timeout` — so without this exemption our own cancellation returns
    // `cause: 'unknown'`, which `integration-health.ts` treats as ACTIONABLE.
    // The equivalent exemption already exists for the direct path below; this
    // keeps the two paths agreeing about whose fault a cancellation is.
    const cancelledByCaller = !controller.signal.aborted && callerSignal?.aborted === true;
    if (isComposioBridgeError(err) && !(cancelledByCaller && err.kind === 'invoke_timeout')) {
      return {
        ok: false,
        status: 0,
        statusText: '',
        body: '',
        reason: 'bridge_error',
        cause: 'unknown',
        error: err.message,
        bridgeKind: err.kind,
      };
    }
    const name = err instanceof Error ? err.name : '';
    const abortish = name === 'AbortError' || name === 'TimeoutError';
    const aborted = abortish && !cancelledByCaller;
    return {
      ok: false,
      status: 0,
      statusText: '',
      body: '',
      reason: aborted ? 'timeout' : 'network_error',
      // Not reaching Composio at all is `unreachable` regardless of which of
      // the two it was; the `reason` keeps the distinction for diagnostics.
      cause: 'unreachable',
      error: err instanceof Error ? err.message : String(err),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * {@link composioFetch}, with the JSON parse every caller was writing by hand.
 *
 * Why it exists: 28 call sites each wrote some variant of
 * `await res.json().catch(() => ({}))`, and the variance was not stylistic —
 * some threw on a malformed body, some silently produced `{}` and then read a
 * field off it. One helper makes the failure mode uniform.
 *
 * `data` is `null` when the call failed OR when the body was not valid JSON.
 * The full result is returned alongside it, so a caller that needs to tell
 * those apart reads `result.ok` — a parse failure on a 200 is a real condition
 * and this must not disguise it as a transport failure.
 */
export async function composioFetchJson<T = unknown>(
  path: string,
  options: ComposioFetchOptions = {},
): Promise<{ result: ComposioFetchResult; data: T | null }> {
  const result = await composioFetch(path, options);
  if (!result.body) return { result, data: null };
  try {
    return { result, data: JSON.parse(result.body) as T };
  } catch {
    return { result, data: null };
  }
}
